import type { BlueMarlinState } from './types';
import { civilFromEpochDay, epochDayFromCivil } from './calendar';
import { BLUE_MARLIN_NORTH_SHARE } from './data/marlin/warm';
import { held } from './held';
import { YEAR_DAYS, type PopulationDraws } from './population';

/**
 * The Pacific blue marlin (M108): the whole Pacific's, one stock, "a
 * pan-Pacific stock caught primarily in tropical and sub-tropical waters"
 * (ISC 2021, p. 2; ISC 2026, its Plenary Report, p. 37), the second
 * population of no sanctuary after the North Atlantic right whales (M107). It
 * belongs to the North Pacific's low band, Maui Nui's, and the South
 * Pacific's, spread over the Pacific's water warmer than 24 °C ("Blue marlin
 * usually inhabit regions where the sea-surface temperatures (SSTs) are
 * greater than 24°C", IATTC-95-05, p. 103; "the dominant role of sea surface
 * temperature in driving distribution", ISC 2026's Plenary Report, p. 30):
 * each band's share in a month is its share of that water that month, the
 * World Ocean Atlas's (`data/marlin/warm.ts`), the North Pacific's 46.5% in
 * February to 67.0% in September (`northShareOn`).
 *
 * Counted by sex and year class since birth, each class aged on the first
 * day of the spawning season; the season's young born on each of its days
 * alike, half of them female, as many as its spawners bear (a Beverton-Holt
 * curve, the assessment's); every fish dying at its age's and sex's natural
 * rate and, from the first 1 May after its season, the fishing rate. Its draws are the
 * caller's, as the right whales' are: binomial deaths and Poisson young on its
 * own stream in the game, expected values in its table (`scripts/marlin.ts`).
 *
 * **Its life is NOAA's newest**: growth, weight, maturity and natural
 * mortality at age as Brodziak's 2026 working paper sets them for the next
 * assessment (PIFSC WP-26-01, CC0), its growth Chang et al.'s 2025 two-stanza
 * curves, its mortality its Lorenzen-scaled maximum-age series, "the default"
 * it recommends (p. 21), the life the ISC's 2026 assessment adopted ("the
 * adoption of newly published sex-specific two-stanza growth curves (Chang et
 * al., 2025)", "Updated sex- and age-specific natural mortality schedules
 * based on Lorenzen scaling with the Hamel and Cope maximum-age approach were
 * also adopted", its Plenary Report, p. 30). **Its fishing and its unfished
 * young are the two numbers fitted**, so its spawning potential at today's
 * fishing is the assessment's for 2021 to 2023 (0.381) and its young a year
 * the assessment's mean (936,000): its fishing a year on fish of one and over
 * (`BLUE_MARLIN_SUPPLY.fishing`) is then not the assessment's "Average F ages
 * 1–10" (0.11 in 2021 to 2023, Table 7-2), whose fleets take each size by
 * their own selectivity, unread (open).
 */

/** One sex's life, each figure with its source. */
export interface MarlinSex {
  /** Chang et al. 2025's juvenile Gompertz curve, birth to age one: β1 (cm), β2, β3 (a year). */
  gompertz: readonly [number, number, number];
  /** Its Schnute curve from age one: the length at one and at the oldest age (cm, eye to fork), and its shape (c, a year). */
  lengthAtOne: number;
  lengthAtOldest: number;
  schnute: number;
  /** Weight at length, kg at cm eye to fork: A·L^B. */
  weightScalar: number;
  weightExponent: number;
  /** Natural mortality a year at each age, 0 to the oldest, the last class's every fish of that age and over. */
  naturalDeaths: readonly number[];
}

/** The Pacific blue marlin's life, each figure with its source. */
export interface BlueMarlinLife {
  id: 'pacific_blue_marlin';
  female: MarlinSex;
  male: MarlinSex;
  /** A female's maturity at length, logistic: its slope (a cm) and the length half are mature at (cm). Only the females' spawning counts. */
  maturitySlope: number;
  maturityLength: number;
  /** The spawning season, [month, day] to [month, day], both in it: classes age on its first day; the young are born on its days alike. */
  season: { from: readonly [number, number]; to: readonly [number, number] };
  /** The Beverton-Holt curve's steepness, and the share of the young female. */
  steepness: number;
  femaleShare: number;
  /** What the fit holds the stock to: the assessment's mean young a year, and its spawning potential at today's fishing (the spawners a young bears fished over unfished). */
  meanRecruits: number;
  spawningPotential: number;
}

/**
 * The Pacific blue marlin, *Makaira nigricans*, each figure with its source as
 * read (M108). Brodziak 2026 is "Natural Mortality Rates for the Pacific Blue
 * Marlin Stock", PIFSC Working Paper WP-26-01 (doi:10.25923/veyk-0393, CC0);
 * ISC 2021 is the ISC Billfish Working Group's "Stock Assessment Report for
 * Pacific Blue Marlin (Makaira nigricans) through 2019", ISC/21/ANNEX/10; ISC
 * 2026 the "Report of the Twenty-Sixth Meeting of the International Scientific
 * Committee for Tuna and Tuna-like Species in the North Pacific Ocean", its
 * Plenary Report, section 7 (each the ISC's own copy, its figures cited, the
 * report not carried: "© ISC", no grant).
 */
export const PACIFIC_BLUE_MARLIN: BlueMarlinLife = {
  id: 'pacific_blue_marlin',
  // Brodziak 2026, Table 1 (p. 25), "Pacific blue marlin parameters used in the Lorenzen-scaled maximum-age estimator",
  // Chang et al. 2025's (ICES J. Mar. Sci. 82(8), fsaf134, Table 5, CC BY 4.0): "Juvenile growth β1 151.6 148.9", "β2 3.45
  // 3.49", "β3 6.44 6.70", "Length at transition age L1 150.8 148.2", "Length at maximum adult age L2 267.2 198.6",
  // "Schnute curve shape parameter c 0.24 0.23", "Maximum adult age t2 27 18", "Length-weight scalar A 0.00001844
  // 0.00001370", "Length-weight exponent B 2.956 2.975"; weight in kg at length in cm EFL (its Figure 4, p. 35).
  // Natural mortality: its Table 4 (p. 28), "Natural mortality rate at age by sex results for the Pacific blue marlin
  // Lorenzen-scaled maximum age estimator", ages 0 to 27 and 0 to 18, the maximum ages (A_max, Table 1).
  female: {
    gompertz: [151.6, 3.45, 6.44],
    lengthAtOne: 150.8,
    lengthAtOldest: 267.2,
    schnute: 0.24,
    weightScalar: 0.00001844,
    weightExponent: 2.956,
    naturalDeaths: [0.40, 0.32, 0.29, 0.26, 0.25, 0.23, 0.22, 0.22, 0.21, 0.21, 0.20, 0.20, 0.20, 0.20, 0.19, 0.19, 0.19, 0.19, 0.19, 0.19, 0.19, 0.19, 0.19, 0.19, 0.19, 0.19, 0.19, 0.19],
  },
  male: {
    gompertz: [148.9, 3.49, 6.70],
    lengthAtOne: 148.2,
    lengthAtOldest: 198.6,
    schnute: 0.23,
    weightScalar: 0.00001370,
    weightExponent: 2.975,
    naturalDeaths: [0.45, 0.37, 0.35, 0.33, 0.32, 0.31, 0.30, 0.30, 0.29, 0.29, 0.29, 0.29, 0.29, 0.28, 0.28, 0.28, 0.28, 0.28, 0.28],
  },
  // Brodziak 2026, Table 1: "Maturity ogive slope s 0.2039", "Length at 50% maturity L50 179.8" (Sun et al. 2009, Fish.
  // Bull. 107: "EFL 50 and EFL 95 ) of 179.76 ±1.01 cm EFL ... and 194.2 ±1.01 cm EFL", p. 425: ln 19 / 14.44 = 0.2039).
  // ISC 2021: "Only the female maturity ogive is conisdered [sic] in Stock Synthesis models" (p. 67).
  maturitySlope: 0.2039,
  maturityLength: 179.8,
  // Sun et al. 2009: "the major spawning season for blue marlin in western Paciﬁc is from May to September" (p. 425);
  // the young of each of its days alike (a choice: no source read gives the season's days' shares).
  season: { from: [5, 1], to: [9, 30] },
  // ISC 2021: "the sex ratio at birth was assumed to be 1:1, and a Beverton-Holt spawner-recruit relationship with
  // steepness (h) fixed at 0.87 was used" (p. 13); ISC 2026 kept it: "The value for the Beverton-Holt stock-recruitment
  // steepness (h) in the base model was 0.87" (its Plenary Report, p. 38). The same 0.87, the 2016 assessment's, in
  // "For blue marlin, the stock recruitment parameters are: ... h = 0.87" (Kapur et al. 2017, PIFSC WP-17-004, p. 37,
  // its NOAA record "CC0 Public Domain").
  steepness: 0.87,
  femaleShare: 0.5,
  // ISC 2026, its Plenary's Table 7-1 (pp. 39-40): "Recruitment (age 0)", "Mean1" 936 thousand, "1 During 1971–2024";
  // Table 7-2 (p. 44): "SPR2021–2023 0.381", the spawning potential at the fishing its projections call recent
  // ("FRecent = F(2021–2023), corresponding to the average fishing intensity (SPR) over 2021–2023", p. 44).
  meanRecruits: 936_000,
  spawningPotential: 0.381,
};

/** The fitted numbers (`scripts/marlin.ts`): the fishing a year on every fish of one and over, and the unfished stock's young a season and its spawning females, kg. */
export interface BlueMarlinSupply {
  fishing: number;
  unfishedRecruits: number;
  unfishedSpawnersKg: number;
}

/** A sex's oldest age, years: its last class's, holding every fish of it and over. */
const oldestAge = (sex: MarlinSex): number => sex.naturalDeaths.length - 1;

/** A fish's length at an age, cm eye to fork: the Gompertz curve to age one, the Schnute curve from it to the oldest age (Brodziak 2026, eqs. 1 and 2, p. 5). */
export function lengthAt(sex: MarlinSex, ageYears: number): number {
  const [b1, b2, b3] = sex.gompertz;
  if (ageYears <= 1) return b1 * Math.exp(-b2 * Math.exp(-b3 * Math.max(0, ageYears)));
  const c = sex.schnute;
  return sex.lengthAtOne * Math.exp(Math.log(sex.lengthAtOldest / sex.lengthAtOne) * (1 - Math.exp(-c * (ageYears - 1))) / (1 - Math.exp(-c * (oldestAge(sex) - 1))));
}

/** A fish's weight at an age, kg. */
export const weightAt = (sex: MarlinSex, ageYears: number): number => sex.weightScalar * lengthAt(sex, ageYears) ** sex.weightExponent;

/** The share of females mature at a length, cm. */
export const maturityAt = (life: BlueMarlinLife, lengthCm: number): number => 1 / (1 + Math.exp(-life.maturitySlope * (lengthCm - life.maturityLength)));

/** The year whose spawning season a day falls after, and the day's number since the season's first day. */
export function spawningYearOf(life: BlueMarlinLife, epochDay: number): { year: number; day: number } {
  const { year } = civilFromEpochDay(epochDay);
  const start = (y: number): number => epochDayFromCivil(y, life.season.from[0], life.season.from[1]);
  const y = epochDay >= start(year) ? year : year - 1;
  return { year: y, day: epochDay - start(y) };
}

/** The spawning season's days, its first to its last (153, 1 May to 30 September). */
export const seasonDays = (life: BlueMarlinLife): number => epochDayFromCivil(2001, life.season.to[0], life.season.to[1]) - epochDayFromCivil(2001, life.season.from[0], life.season.from[1]) + 1;

/** A class's mean age in years on a day of its year: the season's middle day its young's mean birthday. */
const ageOn = (life: BlueMarlinLife, cls: number, day: number): number => cls + (day - (seasonDays(life) - 1) / 2) / YEAR_DAYS;

/** Each female class's spawning weight a fish on the season's first day, kg: its weight times its share mature, the class born that day none. */
const spawningTables = new Map<BlueMarlinLife, number[]>();
function spawningWeights(life: BlueMarlinLife): readonly number[] {
  let t = spawningTables.get(life);
  if (t === undefined) {
    t = life.female.naturalDeaths.map((_, k) => {
      if (k === 0) return 0;
      const age = ageOn(life, k, 0);
      return weightAt(life.female, age) * maturityAt(life, lengthAt(life.female, age));
    });
    spawningTables.set(life, t);
  }
  return t;
}

/** The spawning females' weight, kg, of a population's classes as they stand on the season's first day. */
export function spawnersOf(life: BlueMarlinLife, w: BlueMarlinState): number {
  const sw = spawningWeights(life);
  let kg = 0;
  for (let k = 0; k < w.females.length; k++) kg += (w.females[k] as number) * (sw[k] as number);
  return kg;
}

/** The young a season of spawners bears, by the Beverton-Holt curve through the unfished stock (`supply`). */
export function recruitsOf(life: BlueMarlinLife, supply: BlueMarlinSupply, spawnersKg: number): number {
  const h = life.steepness;
  // The curve in its steepness form, its 4 and 5 by steepness's definition: h of the unfished young at a fifth of the
  // unfished spawners (4h x 0.2 / (1 - h + 0.2 (5h - 1)) = h), all of them at the unfished spawners (4h / 4h = 1).
  return (4 * h * supply.unfishedRecruits * spawnersKg) / (supply.unfishedSpawnersKg * (1 - h) + spawnersKg * (5 * h - 1));
}

/** A fish's chance of dying in a day in a class: its age's natural rate and, from class 1 (the first 1 May after its season, seven to twelve months old), the fishing. */
export function dailyDeathAt(sex: MarlinSex, cls: number, fishing: number): number {
  return 1 - Math.exp(-((sex.naturalDeaths[cls] as number) + (cls >= 1 ? fishing : 0)) / YEAR_DAYS);
}

/** `dailyDeathAt` for each class of each sex, worked out once a life and fishing. */
const deathTables = new Map<BlueMarlinLife, { fishing: number; female: number[]; male: number[] }>();
function deathsOf(life: BlueMarlinLife, fishing: number): { female: number[]; male: number[] } {
  let t = deathTables.get(life);
  if (t === undefined || t.fishing !== fishing) {
    const of = (sex: MarlinSex): number[] => sex.naturalDeaths.map((_, k) => dailyDeathAt(sex, k, fishing));
    t = { fishing, female: of(life.female), male: of(life.male) };
    deathTables.set(life, t);
  }
  return t;
}

/** On the season's first day, every class a year older, each sex's last holding all of its oldest age and over, and the season's spawners weighed; on any other day, nothing. */
function ageBlueMarlin(life: BlueMarlinLife, w: BlueMarlinState, day: number): void {
  if (day !== 0) return;
  for (const c of [w.females, w.males]) {
    const last = c.length - 1;
    c[last] = (c[last] as number) + (c[last - 1] as number);
    for (let k = last - 1; k > 0; k--) c[k] = c[k - 1] as number;
    c[0] = 0;
  }
  // Held, as every stored float is (review M116: its weights' exp, log and powers reached the save to the last bit).
  w.spawnersKg = held(spawnersOf(life, w));
}

/**
 * A day of the blue marlin's lives, the classes aged (`ageBlueMarlin`): in
 * the spawning season, the day's young, an equal share of what the season's
 * spawners bear (`young`, the young a season of spawners, kg: the game's the
 * Beverton-Holt curve, `recruitsOf`), half of them female; then each class of
 * each sex dies at its age's rate and the fishing, the day's young among them.
 */
export function stepBlueMarlin(life: BlueMarlinLife, w: BlueMarlinState, epochDay: number, fishing: number, young: (spawnersKg: number) => number, draws: PopulationDraws): void {
  const { day } = spawningYearOf(life, epochDay);
  ageBlueMarlin(life, w, day);
  const days = seasonDays(life);
  if (day < days) {
    const born = young(w.spawnersKg) / days;
    w.females[0] = (w.females[0] as number) + draws.count(born * life.femaleShare);
    w.males[0] = (w.males[0] as number) + draws.count(born * (1 - life.femaleShare));
  }
  const p = deathsOf(life, fishing);
  for (const [c, rates] of [[w.females, p.female], [w.males, p.male]] as const) {
    for (let k = 0; k < c.length; k++) {
      const n = c[k] as number;
      if (n > 0) c[k] = n - draws.share(n, rates[k] as number);
    }
  }
}

/** The North Pacific's low band's share of the stock on a day: its share of the Pacific's warm water that calendar month (the Atlas's monthly mean); the South Pacific's the rest. */
export const northShareOn = (epochDay: number): number => BLUE_MARLIN_NORTH_SHARE[civilFromEpochDay(epochDay).month - 1] as number;

/**
 * A year of a stock from the day after a day, at expected values on a copy (the game's own step, its young the
 * fitted curve's): the young born, and what the fishing takes, fish and kg: each class's deaths a day, `n·p/(1 - p)`
 * of what the day leaves, the fishing's share of them F/(M + F), at the class's weight that day.
 */
export function marlinYearFrom(life: BlueMarlinLife, w: BlueMarlinState, epochDay: number, supply: BlueMarlinSupply): { young: number; caught: number; caughtKg: number; after: BlueMarlinState } {
  const y: BlueMarlinState = { females: [...w.females], males: [...w.males], spawnersKg: w.spawnersKg };
  let young = 0;
  let caught = 0;
  let caughtKg = 0;
  const draws: PopulationDraws = { share: (n, p) => n * p, count: (mean) => { young += mean; return mean; } };
  const p = deathsOf(life, supply.fishing);
  for (let d = epochDay + 1; d <= epochDay + YEAR_DAYS; d++) {
    stepBlueMarlin(life, y, d, supply.fishing, (kg) => recruitsOf(life, supply, kg), draws);
    const { day } = spawningYearOf(life, d);
    for (const [c, sex, rates] of [[y.females, life.female, p.female], [y.males, life.male, p.male]] as const) {
      for (let k = 1; k < c.length; k++) {
        const q = rates[k] as number;
        const n = (((c[k] as number) * q) / (1 - q)) * (supply.fishing / ((sex.naturalDeaths[k] as number) + supply.fishing));
        caught += n;
        caughtKg += n * weightAt(sex, ageOn(life, k, day));
      }
    }
  }
  return { young, caught, caughtKg, after: y };
}

export interface BlueMarlinCounts {
  population: number;
  females: number;
  males: number;
  /** The fish in the North Pacific's low band today (`northShareOn`, rounded) and in the South Pacific's. */
  north: number;
  south: number;
  /** The fish under a year since their season's first day: this season's young, or last season's before it. */
  young: number;
  /** The spawning females' weight on the season's first day, tonnes. */
  spawnersT: number;
  /** What the fishing takes over the coming year at expected values (`marlinYearFrom`): fish, and tonnes. */
  caughtAYear: number;
  caughtTAYear: number;
}

/** The blue marlin today as the game counts them. */
export function blueMarlinCounts(life: BlueMarlinLife, w: BlueMarlinState, supply: BlueMarlinSupply, epochDay: number): BlueMarlinCounts {
  const sum = (xs: readonly number[]): number => xs.reduce((a, b) => a + b, 0);
  const year = marlinYearFrom(life, w, epochDay, supply);
  const population = sum(w.females) + sum(w.males);
  const north = Math.round(population * northShareOn(epochDay));
  return {
    population,
    females: sum(w.females),
    males: sum(w.males),
    north,
    south: population - north,
    young: (w.females[0] as number) + (w.males[0] as number),
    // In tonnes, 1,000 kg by definition.
    spawnersT: w.spawnersKg / 1000,
    caughtAYear: year.caught,
    caughtTAYear: year.caughtKg / 1000,
  };
}

/** No fish, each class of each sex 0. */
export const emptyBlueMarlin = (life: BlueMarlinLife): BlueMarlinState => ({
  females: life.female.naturalDeaths.map(() => 0),
  males: life.male.naturalDeaths.map(() => 0),
  spawnersKg: 0,
});
